// =============================================================================
// AGC: DROID FARM - Plot Images Page
// AGC: PRO/Premium feature for satellite imagery generation and history
// AGC: Supports NDVI, RGB, and other Sentinel-2 derived imagery
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { 
  ArrowLeft, 
  Satellite, 
  RefreshCw, 
  Clock, 
  Trash2, 
  Download, 
  Leaf, 
  Camera, 
  Image as ImageIcon,
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Layers,
  Calendar,
  Eye
} from 'lucide-react'
import { MapContainer, TileLayer, Polygon, ImageOverlay } from 'react-leaflet'
import { useLotes } from '../../context'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import { GlassCard } from '../shared'
import type { Screen } from '../../types'
import { getBackendUrl } from '../../utils/backendUrl'
import 'leaflet/dist/leaflet.css'

// AGC: Backend API base URL — centralized via getBackendUrl()
const API_BASE_URL = getBackendUrl()

// AGC: Image types that can be generated
type ImageType = 'ndvi' | 'rgb' | 'moisture' | 'chlorophyll'

// AGC: Satellite imagery providers
type ImageProvider = 'sentinel' | 'planetscope' | 'pleiades'

// AGC: Provider configuration
const PROVIDER_CONFIG: Record<ImageProvider, {
  name: string
  resolution: string
  color: string
  bgColor: string
  borderColor: string
  tier: 'free' | 'pro' | 'premium'
  featureKey: 'hasSatelliteImagery' | 'hasPlanetScope' | 'hasAirbusPleiades'
  usageKey: 'sentinelImagesGenerated' | 'planetScopeImagesGenerated' | 'airbusImagesGenerated'
  limitKey: 'maxSentinelImagesPerMonth' | null
  endpoint: string
}> = {
  sentinel: {
    name: 'Sentinel-2',
    resolution: '10m',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
    tier: 'free',
    featureKey: 'hasSatelliteImagery',
    usageKey: 'sentinelImagesGenerated',
    limitKey: 'maxSentinelImagesPerMonth',
    endpoint: '/api/v1/boundaries/ndvi/overlay',
  },
  planetscope: {
    name: 'PlanetScope',
    resolution: '3m',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
    tier: 'pro',
    featureKey: 'hasPlanetScope',
    usageKey: 'planetScopeImagesGenerated',
    limitKey: null, // AGC: Unlimited for now during testing
    endpoint: '/api/v1/boundaries/planetscope/overlay',
  },
  pleiades: {
    name: 'Airbus Pléiades',
    resolution: '50cm',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-300',
    tier: 'pro', // AGC: Changed to PRO - both PRO and Premium have access for testing
    featureKey: 'hasAirbusPleiades',
    usageKey: 'airbusImagesGenerated',
    limitKey: null, // AGC: Unlimited for now during testing
    endpoint: '/api/v1/boundaries/pleiades/overlay',
  },
}

// AGC: Stored image record interface
interface PlotImage {
  id: string
  plotId: string
  type: ImageType
  provider: ImageProvider // AGC: Which satellite provider was used
  imageUrl: string // AGC: The API URL for the overlay
  imageData?: string // AGC: Base64 data URL for cached display
  thumbnailUrl?: string
  createdAt: string
  bounds: {
    minLat: number
    maxLat: number
    minLon: number
    maxLon: number
  }
  // AGC: Leaflet bounds format for ImageOverlay
  leafletBounds: [[number, number], [number, number]] // [[south, west], [north, east]]
  metadata?: {
    acquisitionDate?: string
    cloudCoverage?: number
    resolution?: string
    provider?: string
  }
}

interface PlotImagesPageProps {
  plotId: string
  onBack: () => void
  onNavigate: (screen: Screen, data?: any) => void
}

// AGC: Cache key prefix for localStorage
const IMAGES_CACHE_KEY = 'droidfarm_plot_images_'

// AGC: Get cached images for a plot
// AGC: Migrates older images that don't have leafletBounds or provider
const getCachedImages = (plotId: string): PlotImage[] => {
  try {
    const cached = localStorage.getItem(IMAGES_CACHE_KEY + plotId)
    if (!cached) return []
    
    const images: PlotImage[] = JSON.parse(cached)
    
    // AGC: Migrate old images that don't have leafletBounds or provider
    return images.map(img => {
      let migratedImg = img
      
      // AGC: Migrate leafletBounds
      if (!migratedImg.leafletBounds && migratedImg.bounds) {
        migratedImg = {
          ...migratedImg,
          leafletBounds: [
            [migratedImg.bounds.minLat, migratedImg.bounds.minLon],
            [migratedImg.bounds.maxLat, migratedImg.bounds.maxLon]
          ] as [[number, number], [number, number]]
        }
      }
      
      // AGC: Migrate provider - older images default to sentinel
      if (!migratedImg.provider) {
        migratedImg = {
          ...migratedImg,
          provider: 'sentinel' as ImageProvider
        }
      }
      
      return migratedImg
    })
  } catch {
    return []
  }
}

// AGC: Save images to cache
const cacheImages = (plotId: string, images: PlotImage[]): void => {
  try {
    localStorage.setItem(IMAGES_CACHE_KEY + plotId, JSON.stringify(images))
  } catch (e) {
    console.warn('AGC: Failed to cache images:', e)
  }
}

// AGC: Image type configuration
const IMAGE_TYPE_CONFIG: Record<ImageType, { 
  icon: React.ReactNode
  color: string
  bgColor: string
  description: string
}> = {
  ndvi: {
    icon: <Leaf className="w-5 h-5" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Índice de vegetação normalizado'
  },
  rgb: {
    icon: <Camera className="w-5 h-5" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Imagem colorida real'
  },
  moisture: {
    icon: <Layers className="w-5 h-5" />,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
    description: 'Índice de umidade do solo'
  },
  chlorophyll: {
    icon: <Satellite className="w-5 h-5" />,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    description: 'Concentração de clorofila'
  }
}

/**
 * AGC: PlotImagesPage - Satellite imagery generation and history for a plot
 */
export const PlotImagesPage: React.FC<PlotImagesPageProps> = ({ plotId, onBack, onNavigate }) => {
  const { t } = useTranslation()
  const { getPlotById } = useLotes()
  const { tier, limits, usage, canUseFeature, incrementFeatureUsage } = useSubscription()
  
  // AGC: Get plot data
  const plot = useMemo(() => getPlotById(plotId), [plotId, getPlotById])
  
  // AGC: State
  const [images, setImages] = useState<PlotImage[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState<ImageType | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<PlotImage | null>(null)
  const [expandedTypes, setExpandedTypes] = useState<ImageType[]>(['ndvi'])
  const [selectedProvider, setSelectedProvider] = useState<ImageProvider>('sentinel')
  
  // AGC: Check if user has access to each provider
  const providerAccess = useMemo(() => ({
    sentinel: canUseFeature('hasSatelliteImagery'),
    planetscope: canUseFeature('hasPlanetScope'),
    pleiades: canUseFeature('hasAirbusPleiades'),
  }), [canUseFeature])
  
  // AGC: Get usage stats for each provider
  const providerUsage = useMemo(() => ({
    sentinel: usage?.sentinelImagesGenerated ?? 0,
    planetscope: usage?.planetScopeImagesGenerated ?? 0,
    pleiades: usage?.airbusImagesGenerated ?? 0,
  }), [usage])
  
  // AGC: Get limits for each provider
  const providerLimits = useMemo(() => ({
    sentinel: limits?.maxSentinelImagesPerMonth ?? null,
    planetscope: null, // AGC: Unlimited for now
    pleiades: null, // AGC: Unlimited for now
  }), [limits])
  
  // AGC: Check if user has satellite imagery access
  const hasSatelliteAccess = useMemo(() => {
    return providerAccess.sentinel
  }, [providerAccess])
  
  // AGC: Get available providers based on subscription
  const availableProviders = useMemo(() => {
    const providers: ImageProvider[] = []
    if (providerAccess.sentinel) providers.push('sentinel')
    if (providerAccess.planetscope) providers.push('planetscope')
    if (providerAccess.pleiades) providers.push('pleiades')
    return providers
  }, [providerAccess])
  
  // AGC: Check if current provider can generate more images
  const canGenerateMore = useMemo(() => {
    const config = PROVIDER_CONFIG[selectedProvider]
    if (!providerAccess[selectedProvider]) return false
    
    const limit = providerLimits[selectedProvider]
    if (limit === null) return true // AGC: Unlimited
    
    const used = providerUsage[selectedProvider]
    return used < limit
  }, [selectedProvider, providerAccess, providerLimits, providerUsage])
  
  // AGC: Load cached images on mount
  useEffect(() => {
    if (plotId) {
      const cached = getCachedImages(plotId)
      setImages(cached)
      console.log(`📷 AGC: Loaded ${cached.length} cached images for plot ${plotId}`)
    }
  }, [plotId])
  
  // AGC: Calculate plot bounds from coordinates
  // AGC: Handle both [lat, lng] array format and {lat, lng} object format
  const plotBounds = useMemo(() => {
    if (!plot?.coordinates || plot.coordinates.length < 3) return null
    
    // AGC: Extract lat/lng handling both array and object formats
    const lats: number[] = []
    const lons: number[] = []
    
    plot.coordinates.forEach((c: any) => {
      if (Array.isArray(c)) {
        // AGC: [lat, lng] format (Leaflet standard)
        lats.push(c[0])
        lons.push(c[1])
      } else if (c && typeof c === 'object') {
        // AGC: {lat, lng} object format
        lats.push(c.lat)
        lons.push(c.lng)
      }
    })
    
    if (lats.length === 0 || lons.length === 0) {
      console.error('❌ AGC: Could not extract coordinates from plot:', plot.coordinates)
      return null
    }
    
    const bounds = {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons)
    }
    
    console.log('📐 AGC: Plot bounds calculated:', bounds)
    console.log('📐 AGC: First coordinate sample:', plot.coordinates[0])
    
    return bounds
  }, [plot?.coordinates])
  
  // AGC: Generate image for the plot using selected provider
  const generateImage = useCallback(async (type: ImageType) => {
    if (!plot || !plotBounds) {
      setError(t('fields.images.noCoordinates'))
      return
    }
    
    const providerConfig = PROVIDER_CONFIG[selectedProvider]
    
    // AGC: Check if user has access to this provider
    if (!providerAccess[selectedProvider]) {
      if (selectedProvider === 'planetscope') {
        setError(t('fields.images.planetScopeProRequired'))
      } else if (selectedProvider === 'pleiades') {
        setError(t('fields.images.pleiadesProRequired'))
      } else {
        setError(t('fields.images.proRequired'))
      }
      return
    }
    
    // AGC: Check usage limits for Sentinel-2
    if (!canGenerateMore) {
      setError(t('fields.images.limitReached'))
      return
    }
    
    setGenerating(type)
    setError(null)
    
    try {
      // AGC: Add 10% padding to bounds to ensure full coverage
      const latPadding = (plotBounds.maxLat - plotBounds.minLat) * 0.1
      const lonPadding = (plotBounds.maxLon - plotBounds.minLon) * 0.1
      
      const paddedBounds = {
        minLat: plotBounds.minLat - latPadding,
        maxLat: plotBounds.maxLat + latPadding,
        minLon: plotBounds.minLon - lonPadding,
        maxLon: plotBounds.maxLon + lonPadding
      }
      
      // AGC: Build API request based on provider and image type
      const baseParams = new URLSearchParams({
        minLon: paddedBounds.minLon.toString(),
        minLat: paddedBounds.minLat.toString(),
        maxLon: paddedBounds.maxLon.toString(),
        maxLat: paddedBounds.maxLat.toString(),
        width: '512',
        height: '512'
      })
      
      console.log(`📐 AGC: Original bounds:`, plotBounds)
      console.log(`📐 AGC: Padded bounds:`, paddedBounds)
      console.log(`🛰️ AGC: Provider: ${providerConfig.name} (${providerConfig.resolution})`)
      
      // AGC: Build endpoint URL based on provider
      const imageUrl = `${API_BASE_URL}${providerConfig.endpoint}?${baseParams}`
      
      console.log(`🛰️ AGC: Generating ${type} image for plot ${plot.name} via ${providerConfig.name}...`)
      console.log(`🔗 AGC: URL: ${imageUrl}`)
      
      // AGC: Store the URL as imageData - ImageOverlay will fetch it
      const imageData = imageUrl
      
      // AGC: Calculate Leaflet bounds format [[south, west], [north, east]]
      const leafletBounds: [[number, number], [number, number]] = [
        [paddedBounds.minLat, paddedBounds.minLon], // South-West
        [paddedBounds.maxLat, paddedBounds.maxLon]  // North-East
      ]
      
      // AGC: Create new image record
      const newImage: PlotImage = {
        id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        plotId,
        type,
        provider: selectedProvider, // AGC: Track which provider was used
        imageUrl,
        imageData,
        thumbnailUrl: imageData,
        createdAt: new Date().toISOString(),
        bounds: paddedBounds,
        leafletBounds,
        metadata: {
          acquisitionDate: new Date().toISOString(),
          resolution: providerConfig.resolution,
          provider: providerConfig.name,
        }
      }
      
      // AGC: Add to images list and cache
      const updatedImages = [newImage, ...images]
      setImages(updatedImages)
      cacheImages(plotId, updatedImages)
      
      // AGC: Increment usage counter for this provider
      const usageFeature = selectedProvider === 'sentinel' ? 'sentinelImages' 
        : selectedProvider === 'planetscope' ? 'planetScopeImages' 
        : 'airbusImages'
      
      try {
        await incrementFeatureUsage(usageFeature as any)
        console.log(`📊 AGC: Incremented usage for ${usageFeature}`)
      } catch (usageErr) {
        console.warn('AGC: Failed to increment usage, continuing anyway:', usageErr)
      }
      
      console.log(`✅ AGC: ${type} image generated successfully via ${providerConfig.name}`)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('fields.images.generationError')
      setError(errorMessage)
      console.error(`❌ AGC: Failed to generate ${type} image:`, err)
    } finally {
      setGenerating(null)
    }
  }, [plot, plotBounds, plotId, images, selectedProvider, providerAccess, canGenerateMore, incrementFeatureUsage, t])
  
  // AGC: Delete an image
  const deleteImage = useCallback((imageId: string) => {
    const updatedImages = images.filter(img => img.id !== imageId)
    setImages(updatedImages)
    cacheImages(plotId, updatedImages)
    
    if (selectedImage?.id === imageId) {
      setSelectedImage(null)
    }
    
    console.log(`🗑️ AGC: Deleted image ${imageId}`)
  }, [images, plotId, selectedImage])
  
  // AGC: Calculate optimal resolution based on area size and Sentinel-2 native 10m resolution
  const calculateOptimalResolution = useCallback((bounds: PlotImage['bounds']) => {
    // AGC: Sentinel-2 B04/B08 bands have 10m native resolution
    const SENTINEL_RESOLUTION_METERS = 10
    
    // AGC: Calculate area dimensions in meters
    const latMid = (bounds.minLat + bounds.maxLat) / 2
    const metersPerDegreeLat = 111320
    const metersPerDegreeLon = 111320 * Math.cos(latMid * Math.PI / 180)
    
    const widthMeters = Math.abs(bounds.maxLon - bounds.minLon) * metersPerDegreeLon
    const heightMeters = Math.abs(bounds.maxLat - bounds.minLat) * metersPerDegreeLat
    
    // AGC: Calculate native pixel count at 10m resolution
    const nativeWidth = Math.round(widthMeters / SENTINEL_RESOLUTION_METERS)
    const nativeHeight = Math.round(heightMeters / SENTINEL_RESOLUTION_METERS)
    
    // AGC: Cap at 2500 (Sentinel Hub limit) and ensure minimum of 64
    const width = Math.min(2500, Math.max(64, nativeWidth))
    const height = Math.min(2500, Math.max(64, nativeHeight))
    
    console.log(`📐 AGC: Area size: ${widthMeters.toFixed(0)}m x ${heightMeters.toFixed(0)}m`)
    console.log(`📐 AGC: Native resolution at 10m: ${nativeWidth}x${nativeHeight}px`)
    console.log(`📐 AGC: Download resolution: ${width}x${height}px`)
    
    return { width, height, widthMeters, heightMeters }
  }, [])

  // AGC: Download an image at native Sentinel-2 resolution (10m per pixel)
  const downloadImage = useCallback(async (image: PlotImage) => {
    try {
      console.log(`📥 AGC: Downloading native resolution image ${image.id}...`)
      
      // AGC: Calculate optimal resolution based on actual field size
      const { width, height, widthMeters, heightMeters } = calculateOptimalResolution(image.bounds)
      
      // AGC: Build URL with optimal resolution
      const originalUrl = new URL(image.imageUrl)
      originalUrl.searchParams.set('width', width.toString())
      originalUrl.searchParams.set('height', height.toString())
      const nativeResUrl = originalUrl.toString()
      
      console.log(`🛰️ AGC: Requesting native resolution: ${nativeResUrl}`)
      
      // AGC: Fetch the native resolution image
      const response = await fetch(nativeResUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`)
      }
      
      // AGC: Convert to blob
      const blob = await response.blob()
      console.log(`📦 AGC: Native resolution image: ${(blob.size / 1024).toFixed(1)} KB`)
      
      // AGC: Create object URL for download
      const blobUrl = URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = blobUrl
      // AGC: Include actual resolution and area size in filename
      link.download = `${plot?.name || 'plot'}_${image.type}_${new Date(image.createdAt).toISOString().split('T')[0]}_${width}x${height}px_10m.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // AGC: Cleanup the blob URL
      URL.revokeObjectURL(blobUrl)
      
      console.log(`✅ AGC: Downloaded native resolution image ${image.id}`)
    } catch (error) {
      console.error(`❌ AGC: Failed to download image:`, error)
      setError(t('fields.images.generationError'))
    }
  }, [plot?.name, t, calculateOptimalResolution])
  
  // AGC: Toggle type section expansion
  const toggleTypeExpanded = useCallback((type: ImageType) => {
    setExpandedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    )
  }, [])
  
  // AGC: Get images by type
  const getImagesByType = useCallback((type: ImageType) => {
    return images.filter(img => img.type === type)
  }, [images])
  
  // AGC: Get the most recent image (for preview on LoteDetail)
  const latestImage = useMemo(() => {
    return images.length > 0 ? images[0] : null
  }, [images])
  
  if (!plot) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">{t('fields.images.plotNotFound')}</p>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900">
      {/* AGC: Header */}
      <div className="bg-[#0B5F4A] dark:bg-emerald-800 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-white text-xl font-bold flex items-center gap-2">
              <Satellite className="w-5 h-5" />
              {t('fields.images.title')}
            </h1>
            <p className="text-emerald-200 text-sm">{plot.name}</p>
          </div>
        </div>
      </div>
      
      <div className="px-4 py-4 space-y-4 pb-24">
        {/* AGC: PRO badge indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              hasSatelliteAccess 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {hasSatelliteAccess ? '✨ PRO' : '🔒 FREE'}
            </span>
            <span className="text-sm text-gray-500">
              {images.length} {t('fields.images.imagesCount')}
            </span>
          </div>
        </div>
        
        {/* AGC: Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        {/* AGC: Provider Selector */}
        <GlassCard>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Satellite className="w-5 h-5 text-emerald-600" />
            {t('fields.images.selectProvider')}
          </h2>
          
          {/* AGC: Provider tabs */}
          <div className="flex gap-2 mb-4">
            {(['sentinel', 'planetscope', 'pleiades'] as ImageProvider[]).map((provider) => {
              const config = PROVIDER_CONFIG[provider]
              const hasAccess = providerAccess[provider]
              const isSelected = selectedProvider === provider
              const used = providerUsage[provider]
              const limit = providerLimits[provider]
              
              return (
                <button
                  key={provider}
                  onClick={() => hasAccess && setSelectedProvider(provider)}
                  disabled={!hasAccess}
                  className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? `${config.borderColor} ${config.bgColor}`
                      : hasAccess
                        ? 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                        : 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <span className={`text-xs font-bold ${isSelected ? config.color : 'text-gray-600'}`}>
                    {config.name}
                  </span>
                  <span className={`text-[10px] ${isSelected ? config.color : 'text-gray-500'}`}>
                    {config.resolution}
                  </span>
                  {/* AGC: Usage indicator */}
                  {hasAccess && limit !== null && (
                    <span className={`text-[10px] ${used >= limit ? 'text-red-500' : 'text-gray-400'}`}>
                      {used}/{limit}
                    </span>
                  )}
                  {hasAccess && limit === null && (
                    <span className="text-[10px] text-gray-400">∞</span>
                  )}
                  {!hasAccess && (
                    <span className="text-[10px] text-gray-400 uppercase">
                      {config.tier === 'pro' ? 'PRO' : 'PREMIUM'}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          
          {/* AGC: Current provider info */}
          <div className={`p-3 rounded-lg ${PROVIDER_CONFIG[selectedProvider].bgColor} mb-4`}>
            <div className="flex items-center justify-between">
              <div>
                <span className={`text-sm font-medium ${PROVIDER_CONFIG[selectedProvider].color}`}>
                  {PROVIDER_CONFIG[selectedProvider].name}
                </span>
                <span className="text-xs text-gray-600 ml-2">
                  ({PROVIDER_CONFIG[selectedProvider].resolution} {t('fields.images.resolution')})
                </span>
              </div>
              {/* AGC: Usage counter for Sentinel-2 */}
              {selectedProvider === 'sentinel' && providerLimits.sentinel !== null && (
                <div className={`text-sm font-medium ${
                  providerUsage.sentinel >= providerLimits.sentinel! 
                    ? 'text-red-600' 
                    : 'text-gray-600'
                }`}>
                  {providerUsage.sentinel}/{providerLimits.sentinel} {t('fields.images.usedThisMonth')}
                </div>
              )}
            </div>
          </div>
        </GlassCard>
        
        {/* AGC: Image Generation Section */}
        <GlassCard>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Satellite className="w-5 h-5 text-emerald-600" />
            {t('fields.images.generateNew')}
          </h2>
          
          <div className="grid grid-cols-2 gap-3">
            {/* AGC: NDVI Button */}
            <button
              onClick={() => generateImage('ndvi')}
              disabled={generating !== null || !canGenerateMore}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                canGenerateMore
                  ? 'border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300'
                  : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className={`p-3 rounded-full ${canGenerateMore ? 'bg-green-100' : 'bg-gray-200'}`}>
                {generating === 'ndvi' ? (
                  <RefreshCw className="w-6 h-6 text-green-600 animate-spin" />
                ) : (
                  <Leaf className={`w-6 h-6 ${canGenerateMore ? 'text-green-600' : 'text-gray-400'}`} />
                )}
              </div>
              <span className={`text-sm font-medium ${canGenerateMore ? 'text-gray-900' : 'text-gray-500'}`}>
                NDVI
              </span>
              <span className="text-xs text-gray-500">{t('fields.images.ndviDescription')}</span>
            </button>
            
            {/* AGC: RGB Button */}
            <button
              onClick={() => generateImage('rgb')}
              disabled={generating !== null || !canGenerateMore}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                canGenerateMore
                  ? 'border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300'
                  : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
              }`}
            >
              <div className={`p-3 rounded-full ${canGenerateMore ? 'bg-blue-100' : 'bg-gray-200'}`}>
                {generating === 'rgb' ? (
                  <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
                ) : (
                  <Camera className={`w-6 h-6 ${canGenerateMore ? 'text-blue-600' : 'text-gray-400'}`} />
                )}
              </div>
              <span className={`text-sm font-medium ${canGenerateMore ? 'text-gray-900' : 'text-gray-500'}`}>
                RGB
              </span>
              <span className="text-xs text-gray-500">{t('fields.images.rgbDescription')}</span>
            </button>
          </div>
          
          {/* AGC: Limit reached warning */}
          {!canGenerateMore && hasSatelliteAccess && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-700 font-medium mb-2">
                ⚠️ {t('fields.images.limitReachedTitle')}
              </p>
              <p className="text-xs text-orange-600 mb-3">
                {t('fields.images.limitReachedDescription')}
              </p>
              <button
                onClick={() => onNavigate('subscription')}
                className="w-full px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                {t('fields.images.upgradeCta')}
              </button>
            </div>
          )}
          
          {/* AGC: Upgrade prompt for free users */}
          {!hasSatelliteAccess && (
            <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700 font-medium mb-2">
                🚀 {t('fields.images.upgradeTitle')}
              </p>
              <p className="text-xs text-purple-600 mb-3">
                {t('fields.images.upgradeDescription')}
              </p>
              <button
                onClick={() => onNavigate('subscription')}
                className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                {t('fields.images.upgradeCta')}
              </button>
            </div>
          )}
        </GlassCard>
        
        {/* AGC: Image History Section */}
        <GlassCard>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            {t('fields.images.history')}
          </h2>
          
          {images.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{t('fields.images.noImages')}</p>
              {hasSatelliteAccess && (
                <p className="text-gray-400 text-xs mt-1">{t('fields.images.generateFirst')}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* AGC: Group images by type */}
              {(['ndvi', 'rgb', 'moisture', 'chlorophyll'] as ImageType[]).map(type => {
                const typeImages = getImagesByType(type)
                if (typeImages.length === 0) return null
                
                const config = IMAGE_TYPE_CONFIG[type]
                const isExpanded = expandedTypes.includes(type)
                
                return (
                  <div key={type} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* AGC: Type header */}
                    <button
                      onClick={() => toggleTypeExpanded(type)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${config.bgColor}`}>
                          <span className={config.color}>{config.icon}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{type.toUpperCase()}</p>
                          <p className="text-xs text-gray-500">{typeImages.length} {t('fields.images.imagesCount')}</p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {/* AGC: Images grid */}
                    {isExpanded && (
                      <div className="p-3 grid grid-cols-1 gap-3">
                        {typeImages.map(image => (
                          <div
                            key={image.id}
                            className="relative group rounded-lg overflow-hidden border border-gray-200"
                          >
                            {/* AGC: Mini-map with NDVI overlay - proper visualization */}
                            <div className="h-48 relative">
                              <MapContainer
                                bounds={image.leafletBounds}
                                style={{ height: '100%', width: '100%' }}
                                zoomControl={false}
                                dragging={false}
                                scrollWheelZoom={false}
                                doubleClickZoom={false}
                                attributionControl={false}
                              >
                                {/* AGC: Satellite base layer */}
                                <TileLayer
                                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                  maxZoom={19}
                                />
                                
                                {/* AGC: NDVI overlay on top of satellite */}
                                {image.imageData && (
                                  <ImageOverlay
                                    url={image.imageData}
                                    bounds={image.leafletBounds}
                                    opacity={0.7}
                                    zIndex={100}
                                  />
                                )}
                                
                                {/* AGC: Plot boundary outline */}
                                {plot?.coordinates && (
                                  <Polygon
                                    positions={plot.coordinates}
                                    pathOptions={{
                                      color: '#F59E0B',
                                      fillColor: 'transparent',
                                      fillOpacity: 0,
                                      weight: 2,
                                    }}
                                  />
                                )}
                              </MapContainer>
                              
                              {/* AGC: View full button */}
                              <button
                                onClick={() => setSelectedImage(image)}
                                className="absolute top-2 left-2 p-1.5 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                              >
                                <Eye className="w-4 h-4 text-white" />
                              </button>
                            </div>
                            
                            {/* AGC: Date and actions bar */}
                            <div className="flex items-center justify-between p-2 bg-gray-50">
                              <p className="text-gray-700 text-xs flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(image.createdAt).toLocaleDateString('pt-BR')}
                              </p>
                              
                              {/* AGC: Action buttons */}
                              <div className="flex gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    downloadImage(image)
                                  }}
                                  className="p-1.5 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                                >
                                  <Download className="w-4 h-4 text-gray-700" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    deleteImage(image.id)
                                  }}
                                  className="p-1.5 bg-white rounded-full shadow hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </GlassCard>
      </div>
      
      {/* AGC: Full-screen image viewer modal with map */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-[90] flex flex-col"
          onClick={() => setSelectedImage(null)}
        >
          {/* AGC: Header - safe-area-top so it sits below the Android status bar */}
          <div className="flex items-center justify-between px-4 pb-4 pt-safe-4 bg-black/50">
            <div className="text-white">
              <p className="font-medium">{selectedImage.type.toUpperCase()} - {plot?.name}</p>
              <p className="text-sm text-gray-300">
                {new Date(selectedImage.createdAt).toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  downloadImage(selectedImage)
                }}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <Download className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  deleteImage(selectedImage.id)
                }}
                className="p-2 bg-red-500/50 rounded-full hover:bg-red-500/70 transition-colors"
              >
                <Trash2 className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => setSelectedImage(null)}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <span className="text-white text-xl">✕</span>
              </button>
            </div>
          </div>
          
          {/* AGC: Full-screen map with NDVI overlay */}
          <div className="flex-1" onClick={(e) => e.stopPropagation()}>
            <MapContainer
              bounds={selectedImage.leafletBounds}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
              dragging={true}
              scrollWheelZoom={true}
            >
              {/* AGC: Satellite base layer */}
              <TileLayer
                attribution='Tiles &copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              
              {/* AGC: NDVI overlay */}
              {selectedImage.imageData && (
                <ImageOverlay
                  url={selectedImage.imageData}
                  bounds={selectedImage.leafletBounds}
                  opacity={0.7}
                  zIndex={100}
                />
              )}
              
              {/* AGC: Plot boundary */}
              {plot?.coordinates && (
                <Polygon
                  positions={plot.coordinates}
                  pathOptions={{
                    color: '#F59E0B',
                    fillColor: 'transparent',
                    fillOpacity: 0,
                    weight: 3,
                  }}
                />
              )}
            </MapContainer>
          </div>
          
          {/* AGC: NDVI Legend */}
          <div className="absolute bottom-4 left-4 bg-gray-900/90 rounded-lg p-3 text-xs z-[1000]">
            <div className="font-medium text-white mb-2 flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Legenda NDVI
            </div>
            <div className="space-y-1">
              {[
                { color: '#8b5a2b', label: 'Solo exposto', range: '< 0' },
                { color: '#c8a064', label: 'Veg. esparsa', range: '0-0.2' },
                { color: '#d4d464', label: 'Veg. moderada', range: '0.2-0.4' },
                { color: '#64c864', label: 'Saudável', range: '0.4-0.6' },
                { color: '#32a032', label: 'Muito saudável', range: '0.6-0.8' },
                { color: '#1e6e1e', label: 'Densa', range: '> 0.8' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-3 rounded"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-gray-300">{item.label}</span>
                  <span className="text-gray-500 ml-auto">{item.range}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// AGC: Export helper to get latest image for a plot (used by LoteDetail preview)
export const getLatestPlotImage = (plotId: string): PlotImage | null => {
  const images = getCachedImages(plotId)
  return images.length > 0 ? images[0] : null
}

export default PlotImagesPage

